import { useMemo, useState } from 'react';
import type { HistoryResponse, LeagueBundle } from '../../shared/types';
import { Avatar, Empty, ErrorState, Loading, PageHead, Section } from '../components/common';
import { useScores } from '../lib/api';
import { fmtPct, fmtPoints } from '../lib/format';
import { managerDirectory, type ManagerRef } from '../lib/rivalries';
import {
  efficiencyBoard,
  flattenHistory,
  seasonBoards,
  singleGameBoards,
  streakBoards,
  type RecordBoard,
  type RecordRow,
  type ValueKind,
} from '../lib/records';

function headline(kind: ValueKind, row: RecordRow): string {
  switch (kind) {
    case 'points':
      return fmtPoints(row.value);
    case 'pct':
      return fmtPct(row.value, 1);
    case 'count':
      return String(row.value);
    case 'record':
      // The win percentage is the sort key, but "12-2" is the record.
      return row.detail ?? fmtPct(row.value, 1);
  }
}

function when(row: RecordRow): string {
  const week = row.week != null ? ` · Week ${row.week}` : '';
  return `${row.season}${week}`;
}

function Board({
  board,
  dir,
}: {
  board: RecordBoard;
  dir: Map<string, ManagerRef>;
}) {
  const [top, ...rest] = board.rows;
  const name = (id: string) => dir.get(id)?.name ?? 'Unknown';

  return (
    <article className="card card-pad record-card">
      <div className="record-head">
        <h3>{board.title}</h3>
        <span className="hint">{board.hint}</span>
      </div>

      {!top ? (
        <div className="faint" style={{ fontSize: 13, padding: '10px 0' }}>
          Not enough games yet.
        </div>
      ) : (
        <>
          <div className="record-hero">
            <div className="record-value">{headline(board.kind, top)}</div>
            <div className="record-holder">
              <Avatar id={dir.get(top.userId)?.avatar ?? null} alt={name(top.userId)} />
              <div style={{ minWidth: 0 }}>
                <div className="team-title">{name(top.userId)}</div>
                <div className="team-handle">
                  {when(top)}
                  {top.isPlayoff && <span className="pill pill-ember tiny">playoff</span>}
                </div>
              </div>
            </div>
          </div>

          {/* For a 'record' board the detail IS the headline ("11-5"), so
              printing it again below would just repeat the number. */}
          {(() => {
            const detail = top.detail === headline(board.kind, top) ? undefined : top.detail;
            if (!detail && !top.opponentId) return null;
            return (
              <div className="record-note">
                {detail}
                {top.opponentId && `${detail ? ' · ' : ''}vs ${name(top.opponentId)}`}
              </div>
            );
          })()}

          {rest.length > 0 && (
            <ol className="record-rest">
              {rest.map((r, i) => (
                <li key={`${r.userId}-${r.season}-${r.week}-${i}`}>
                  <span className="record-rank">{i + 2}</span>
                  <span className="record-rest-value tabnum">{headline(board.kind, r)}</span>
                  <span className="record-rest-name">{name(r.userId)}</span>
                  <span className="record-rest-when">{when(r)}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </article>
  );
}

export default function Records({
  bundle,
  history,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
}) {
  const [includePlayoffs, setIncludePlayoffs] = useState(true);

  // Same fan-out as Rivalries. `shared()` dedupes the requests, so visiting
  // both pages in one session costs one set of fetches, not two.
  const leagueIds = useMemo(() => {
    const ids = [bundle.league.league_id];
    for (const s of history?.seasons ?? []) ids.push(s.league_id);
    return ids;
  }, [bundle.league.league_id, history]);

  const { data, error, loading, reload } = useScores(leagueIds);
  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);

  const flat = useMemo(
    () => flattenHistory(data ?? [], includePlayoffs),
    [data, includePlayoffs],
  );

  const single = useMemo(() => singleGameBoards(flat), [flat]);
  const season = useMemo(() => seasonBoards(flat.appearances), [flat]);
  const streaks = useMemo(() => streakBoards(flat.appearances), [flat]);
  const efficiency = useMemo(() => efficiencyBoard(bundle, history), [bundle, history]);

  if (loading) return <Loading label="Loading every game ever played" />;
  if (error || !data) {
    return <ErrorState message={error ?? 'No score data returned'} onRetry={reload} />;
  }

  if (flat.meetings.length === 0) {
    return (
      <>
        <PageHead title="Records" />
        <div className="card">
          <Empty>
            No games played yet{includePlayoffs ? '' : ' outside the playoffs'}.
          </Empty>
        </div>
      </>
    );
  }

  const seasonsPlayed = new Set(flat.meetings.map((m) => m.season)).size;

  return (
    <>
      <PageHead
        title="Records"
        meta={[
          `${flat.meetings.length} games`,
          seasonsPlayed === 1 ? 'one season' : `${seasonsPlayed} seasons`,
          'By manager, not roster',
        ]}
      />

      <div className="toolbar">
        <button
          className={`chip ${includePlayoffs ? 'on' : ''}`}
          onClick={() => setIncludePlayoffs(true)}
        >
          All games
        </button>
        <button
          className={`chip ${!includePlayoffs ? 'on' : ''}`}
          onClick={() => setIncludePlayoffs(false)}
        >
          Regular season only
        </button>
      </div>

      <Section title="Single Game" hint="One team, one week">
        <div className="grid grid-2 grid-start">
          {single.map((b) => (
            <Board key={b.key} board={b} dir={dir} />
          ))}
        </div>
      </Section>

      <Section title="Season">
        <div className="grid grid-2 grid-start">
          {season.map((b) => (
            <Board key={b.key} board={b} dir={dir} />
          ))}
          <Board board={efficiency} dir={dir} />
        </div>
        <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
          Efficiency is scored versus optimal. Sleeper reports it per season, so it ignores the
          playoff toggle.
        </p>
      </Section>

      <Section title="Streaks" hint="Chronological, across seasons">
        <div className="grid grid-2 grid-start">
          {streaks.map((b) => (
            <Board key={b.key} board={b} dir={dir} />
          ))}
        </div>
      </Section>
    </>
  );
}
